import type {
  AccessibleSupportTeam,
  AssignableUser,
} from "@redotech/effect-api-shared/rpc/support-ticket-related-records";
import type { TagResponse } from "@redotech/effect-api-shared/rpc/tag";
import {
  SupportTicketFilterNames,
  supportTicketFilterData,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import FileIcon from "@redotech/redo-icons/file-01.svg";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import type { RedoPlatformRpcClient } from "@redotech/redo-merchant-platform-rpc-client/client";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { NumberFilterOperator } from "@redotech/redo-views-common/advanced-filters/number-filter";
import type {
  AdvancedTableFilter,
  NumberTableFilter,
} from "@redotech/redo-web/advanced-filters/advanced-filter";
import type { QueryClient } from "@tanstack/react-query";
import { createAssigneesAdvancedFilter } from "./assignees-filter";
import { createChannelsAdvancedFilter } from "./channels-filter";
import { ClosedAtAdvancedFilter } from "./closed-at-filter";
import { CreatedAtAdvancedFilter } from "./created-at-filter";
import { createCustomerAdvancedFilter } from "./customer-filter";
import { createInboxEmailAdvancedFilter } from "./inbox-email-filter";
import { LastCustomerMessageAtAdvancedFilter } from "./last-customer-message-at-filter";
import { LastMerchantMessageAtAdvancedFilter } from "./last-merchant-message-at-filter";
import { LastResponseAtAdvancedFilter } from "./last-response-at-filter";
import { createMentionsAdvancedFilter } from "./mentions-filter";
import { ReadStatusAdvancedFilter } from "./read-status-filter";
import { SearchContainsAdvancedFilter } from "./search-contains-filter";
import { StatusAdvancedFilter } from "./status-filter";
import { createSupportTicketTagsAdvancedFilter } from "./support-ticket-tags-filter";
import { createTeamsAdvancedFilter } from "./teams-filter";
import { WordsAdvancedFilter } from "./words-filter";

const NumDraftsFilter: NumberTableFilter = {
  type: AdvancedFilterType.NUMBER,
  Icon: FileIcon,
  operators: [NumberFilterOperator.GREATER_THAN],
  data: supportTicketFilterData[SupportTicketFilterNames.NUM_DRAFTS],
  displayName: "Drafts",
};

const NumSentMessagesFilter: NumberTableFilter = {
  type: AdvancedFilterType.NUMBER,
  Icon: FileIcon,
  operators: [NumberFilterOperator.GREATER_THAN],
  data: supportTicketFilterData[SupportTicketFilterNames.NUM_SENT_MESSAGES],
  displayName: "Sent Messages",
};

export function getSupportTicketAdvancedFilters(options?: {
  effectApi?: EffectApiContext;
  platformRpc?: RedoPlatformRpcClient;
  queryClient?: QueryClient;
  tags?: TagResponse[];
  accessibleTeams?: readonly AccessibleSupportTeam[];
  users?: readonly AssignableUser[];
  teamId?: string;
}): AdvancedTableFilter[] {
  const accessibleTeams = options?.accessibleTeams ?? [];
  // Only offer the team filter when the caller actually has connected teams to
  // filter between — a single-team merchant has nothing to choose from.
  const teamsFilter =
    accessibleTeams.length > 1
      ? [createTeamsAdvancedFilter(accessibleTeams)]
      : [];

  return [
    StatusAdvancedFilter,
    ...teamsFilter,
    createChannelsAdvancedFilter(),
    createInboxEmailAdvancedFilter(options?.effectApi),
    createAssigneesAdvancedFilter(options?.users),
    createCustomerAdvancedFilter(options?.effectApi, options?.teamId),
    createSupportTicketTagsAdvancedFilter(options?.tags ?? []),
    createMentionsAdvancedFilter(options?.queryClient, options?.platformRpc),
    ReadStatusAdvancedFilter,
    WordsAdvancedFilter,
    SearchContainsAdvancedFilter,
    CreatedAtAdvancedFilter,
    ClosedAtAdvancedFilter,
    LastResponseAtAdvancedFilter,
    LastCustomerMessageAtAdvancedFilter,
    LastMerchantMessageAtAdvancedFilter,
    // Internal filters for default views (Drafts, Sent) - not shown in filter UI
    NumDraftsFilter,
    NumSentMessagesFilter,
  ];
}
